import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { map, Observable } from 'rxjs';
import {
  AdminCreateCustomerDto,
  AdminCreateCustomerResult,
  AdminCustomersFacade,
  AdminCustomerUpdateDto,
} from './admin-customers.facade';
import { AdminCustomerListItem } from '@models/resources/admin-customer.model';
import { AdminTransactionListItemV4 } from '@models/resources/admin-transaction.model';
import { environment } from '../../../../environments/environment';

/**
 * HTTP-Variante – `POST /api/customers` ist seit Sprint 5b S5b-7 live
 * und wird von S6-12 verdrahtet. Sprint-6-folgende Stories liefern die
 * weiteren Endpoints (Liste/Get/Update/Transactions).
 */
@Injectable()
export class HttpAdminCustomersFacade implements AdminCustomersFacade {
  private readonly http = inject(HttpClient);
  private readonly base = `${environment.apiUrl}/customers`;

  list(): Observable<AdminCustomerListItem[]> {
    return this.http.get<AdminCustomerListItem[]>(this.base);
  }

  get(id: number): Observable<AdminCustomerListItem> {
    return this.http.get<AdminCustomerListItem>(`${this.base}/${id}`);
  }

  update(
    id: number,
    dto: AdminCustomerUpdateDto,
  ): Observable<AdminCustomerListItem> {
    return this.http.put<AdminCustomerListItem>(`${this.base}/${id}`, dto);
  }

  listTransactions(customerId: number): Observable<AdminTransactionListItemV4[]> {
    // Re-use des Admin-TX-Liste-Endpoints mit `customer_id`-Filter
    // (Polish-Endpoint aus Sprint 6 Post-Sprint). Saves uns einen
    // dedizierten `/api/customers/{id}/transactions`-Endpoint.
    const params = new HttpParams().set('customer_id', String(customerId));
    return this.http.get<AdminTransactionListItemV4[]>(
      `${environment.apiUrl}/transaction-requests`,
      { params },
    );
  }

  create(dto: AdminCreateCustomerDto): Observable<AdminCreateCustomerResult> {
    // BE antwortet `{ message, data: { user_id, customer_id } }` — wir
    // extrahieren das Data-Sub-Object für ein flaches FE-Result.
    return this.http
      .post<{ message: string; data: AdminCreateCustomerResult }>(this.base, dto)
      .pipe(map((res) => res.data));
  }

  resendActivation(customerId: number): Observable<void> {
    return this.http
      .post<{ message: string }>(`${this.base}/${customerId}/resend-activation`, {})
      .pipe(map(() => undefined));
  }
}
